import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { execApi } from '../../../services/api';

// Psychiatrist capacity-forecast panel (Ohad 2026-06-30): one card per doctor —
// load% next to the name + 4 month rectangles coloured by follow-up demand vs supply.
// Click a card → the per-month patient list + the prescription ("open N slots").

interface MonthCell {
  month: string; demand: number; scheduled: number; gap: number; gap_hours: number; level: string;
  fu_hours: number; intake_hours: number; intake_capacity: number; overflow: boolean; shortfall_hours: number;
  given_hours: number; work_hours: number; free_hours: number; scheduled_meetings: number;
}
interface Routing { month: string; intake_capacity: number; doctors_open: number }
// Shachar cumulative card (2026-07-14): one cell per month, all hours.
interface TL {
  month: string; is_future: boolean;
  queue_hours: number; queue_est_hours: number;
  done_fu_hours: number; done_intake_hours: number;
  sched_fu_hours: number; sched_intake_hours: number;
  free_hours: number; delay_days: number | null;
  cumulative_hours: number; avg_hours: number; level: string;
}
interface Doctor {
  name: string; active_patients: number; waiting_intake: number; intakes_this_month: number;
  given_hours: number; capacity_hours: number; sustainable: number; load_pct: number | null; load_level: string;
  on_vacation: boolean; has_left: boolean; accepts_maccabi: boolean; avg_capacity_hours: number;
  months: MonthCell[]; timeline: TL[];
}
interface ForecastResp {
  as_of: string; cadence_fu_days: number; cadence_intake_days: number;
  fu_minutes: number; intake_minutes: number;
  months: string[]; doctors: Doctor[]; routing: Routing[];
}

const HE_MON = ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ', 'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];
const monLabel = (id: string) => HE_MON[parseInt(id.slice(5, 7), 10) - 1] ?? id.slice(5);

const LEVEL_COLOR: Record<string, string> = {
  green: '#10B981', yellow: '#F59E0B', orange: '#F59E0B', red: '#EF4444',
};

export function ExecForecast() {
  const [data, setData] = useState<ForecastResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Doctor | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    execApi.getCapacityForecast()
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError('שגיאה בטעינת תחזית הקיבולת'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Clinic-level overview: how many doctors are in cumulative deficit (red) + the deepest hole.
  const overview = useMemo(() => {
    if (!data) return null;
    const red = data.doctors.filter((d) => d.timeline.some((c) => c.is_future && c.level === 'red')).length;
    let worstDoc = '', worstCum = 0;
    data.doctors.forEach((d) => {
      const last = d.timeline[d.timeline.length - 1];
      if (last && last.cumulative_hours < worstCum) { worstCum = last.cumulative_hours; worstDoc = d.name; }
    });
    return { red, worstDoc, worstCum: Math.round(worstCum) };
  }, [data]);
  // worst future cumulative colour per doctor → card accent
  const cardLevel = (d: Doctor): string => {
    const fut = d.timeline.filter((c) => c.is_future);
    if (fut.some((c) => c.level === 'red')) return 'red';
    if (fut.some((c) => c.level === 'orange')) return 'orange';
    return 'green';
  };

  if (loading) return <div className="p-6 text-gray-500">טוען תחזית…</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;
  if (!data) return null;

  return (
    <div dir="rtl">
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <button onClick={() => navigate('/exec/staff')} style={{ background: 'none', border: '1px solid var(--ex-border)', color: 'var(--ex-text-secondary)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>← דריל כוח-אדם</button>
      </div>

      {/* Overview line */}
      {overview && (
        <div className="pat-unified"><div className="pat-unified-section">
          <div className="pat-panel-head">
            <span className="pat-panel-title">תחזית קיבולת פסיכיאטרים — מודל מצטבר (ביקוש מעקבים מול קיבולת פנויה)</span>
            <span className="pat-panel-sub">
              {overview.red} פסיכיאטרים בגירעון מצטבר (🔴 הבור עמוק מהתפוקה החודשית) ·
              {' '}הבור העמוק ביותר: {overview.worstDoc} ({overview.worstCum} ש׳) ·
              {' '}צבע = |מצטבר| ÷ ממוצע-3-חודשים
            </span>
          </div>
        </div></div>
      )}

      {/* Clinic routing strip — new-patient (intake) capacity per month */}
      <div style={{ display: 'flex', gap: 8, margin: '12px 0 18px', flexWrap: 'wrap' }}>
        {data.routing.map((r) => (
          <div key={r.month} style={{
            flex: '1 1 120px', background: 'var(--ex-bg-panel)', border: '1px solid var(--ex-border)',
            borderRadius: 8, padding: '10px 12px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 12, color: 'var(--ex-text-muted)' }}>{monLabel(r.month)} — קליטת פונים חדשים</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: r.intake_capacity > 0 ? '#047857' : '#EF4444' }}>{r.intake_capacity}</div>
            <div style={{ fontSize: 11, color: 'var(--ex-text-muted)' }}>{r.doctors_open} רופאים פתוחים לאינטייק</div>
          </div>
        ))}
      </div>

      {/* Doctor cards — compact overview (current + next 3). Click → wide doctor drill. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(460px, 1fr))', gap: 16 }}>
        {data.doctors.map((d) => (
          <div key={d.name}
            onClick={() => setSelected(d)}
            title="לחצו לדריל הרופא — ציר זמן מלא + נוסחאות"
            style={{
              background: 'var(--ex-bg-panel)', border: `1px solid ${LEVEL_COLOR[cardLevel(d)]}40`,
              borderRight: `4px solid ${LEVEL_COLOR[cardLevel(d)]}`,
              borderRadius: 12, padding: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.25)', cursor: 'pointer',
            }}>
            {/* Name + load% */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: 'var(--ex-text-primary)', fontSize: 15 }}>{d.name}</span>
                {!d.accepts_maccabi && (
                  <span title="לא מוסמך למכבי — אין אינטייק מכבי ב-6 החודשים האחרונים"
                    style={{
                      fontSize: 10, fontWeight: 700, color: '#8B5CF6',
                      background: '#8b5cf620', border: '1px solid #8b5cf655',
                      borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap',
                    }}>ללא מכבי</span>
                )}
              </span>
              {d.has_left ? (
                <span title="אין פגישות עתידיות ואין שעות — כנראה עזב את המרפאה. המטופלים הפעילים שלו זקוקים לשיוך מחדש"
                  style={{
                    fontWeight: 800, fontSize: 13, color: '#FFFFFF', background: '#EF4444',
                    borderRadius: 6, padding: '2px 10px', whiteSpace: 'nowrap',
                  }}>🚪 עזב</span>
              ) : d.on_vacation ? (
                <span title="אין שעות עבודה או פגישות שנותרו החודש, אך חוזר בהמשך — בחופש. העומס הנוכחי לא מחושב (המכנה אפסי)"
                  style={{
                    fontWeight: 800, fontSize: 13, color: 'var(--ex-text-secondary)', background: 'var(--ex-bg-card)',
                    border: '1px dashed var(--ex-border)', borderRadius: 6, padding: '2px 10px', whiteSpace: 'nowrap',
                  }}>🌴 בחופש</span>
              ) : d.load_pct != null ? (
                <span title={`עומס נוכחי (${data.months[0].slice(5)}): (${d.active_patients} פעילים + ${d.intakes_this_month} אינטייקים החודש) ÷ (${d.capacity_hours}ש׳ עבודה × 7.5)`}
                  style={{
                    fontWeight: 800, fontSize: 14, color: '#FFFFFF', background: LEVEL_COLOR[d.load_level],
                    borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
                  }}>{d.load_pct}%<span style={{ fontSize: 9, fontWeight: 600, opacity: 0.9 }}> עכשיו</span></span>
              ) : (
                <span title="אין שעות נפתחו בטבלת כוח-האדם — אין נתוני קיבולת" style={{
                  fontWeight: 700, fontSize: 12, color: 'var(--ex-text-muted)', background: 'var(--ex-bg-card)',
                  border: '1px dashed var(--ex-border)', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap',
                }}>אין שעות</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ex-text-muted)', marginTop: 4 }}>
              {d.active_patients} פעילים · {d.intakes_this_month} אינטייקים החודש · {d.waiting_intake} בתור ·{' '}
              <b style={{ color: 'var(--ex-text-secondary)' }}>נותן ~{d.avg_capacity_hours}ש׳/חודש</b>
              {' '}<span title="ממוצע השעות שהרופא מספק בפועל ב-3 החודשים האחרונים — בסיס הקיבולת היציב">ⓘ</span>
            </div>

            {/* Overview: current + next 3 (future). The whole card is clickable → doctor drill. */}
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              {d.timeline.filter((c) => data.months.includes(c.month)).map((c) => <MonthBox key={c.month} c={c} />)}
            </div>
          </div>
        ))}
      </div>

      {/* Deep timeline drawer — full history + forward (slide-2 popup) */}
      {selected && (
        <>
          <div onClick={() => setSelected(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }} />
          <div style={{
            position: 'fixed', top: 0, bottom: 0, left: 0, width: 'min(900px, 98vw)', zIndex: 41,
            background: '#F7F9FC', borderLeft: '1px solid #E2E8F0',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.35)', padding: 20, overflowY: 'auto', color: '#101D35',
          }} dir="rtl">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontWeight: 800, color: '#0f172a' }}>{selected.name}</h3>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#64748B', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: '#475569' }}>
              {selected.active_patients} מטופלים פעילים · עומס נוכחי {selected.load_pct ?? '—'}% ·
              {' '}נותן ~{selected.avg_capacity_hours}ש׳/חודש · מודל מצטבר (מעקב 15ד׳ · אינטייק 30ד׳)
            </div>

            {/* Light month boxes — May onward, wrapped into rows (no horizontal scroll) */}
            <div style={{ marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'stretch' }}>
              {selected.timeline.filter((c) => c.month >= '2026-05').map((c) => <MonthBox key={c.month} c={c} light />)}
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: '#475569', lineHeight: 1.7 }}>
              <b>תור</b> = גרר-קודם ×0.80(מחזור) + ביקוש-חדש (need). <b>משוער</b> = אינטייקים(−חודשיים)×0.55.<br />
              <b>מצטבר</b> = min(0, −(תור + משוער − סופק)) — הבור מקוזז רק ע"י מעקבים שבאמת נקבעו/בוצעו.<br />
              <b>פנוי</b> = שעות פנויות אינפורמטיבי (המנוף): יש פנוי + בור אדום → סגור אינטייקים → יתמלא מעקבים → הבור ירד.<br />
              <b>צבע</b> = |מצטבר| ÷ ממוצע-שעות (3 חודשים): &gt;1 🔴 · 0.5–1 🟠 · &lt;0.5 🟢.<br />
              <b>איחור</b> = ממוצע ההפרש בין המועד שנקבע למועד שהיה צריך (need→next).<br />
              {selected.has_left && <span style={{ color: '#EF4444' }}>⚠ הרופא כנראה עזב — המטופלים זקוקים לשיוך מחדש.<br /></span>}
              רשימת מטופלים פר-חודש — בקרוב.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MonthBox({ c, light }: { c: TL; light?: boolean }) {
  const bg = LEVEL_COLOR[c.level];
  const done = (c.done_fu_hours || c.done_intake_hours) ? `${c.done_fu_hours}/${c.done_intake_hours}` : '—';
  return (
    <div style={{
      minWidth: 112, flex: light ? '1 0 150px' : '1 0 112px', borderRadius: 8, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      border: c.is_future ? `2px solid ${bg}` : `1px solid ${light ? '#E2E8F0' : 'var(--ex-border)'}`,
      background: light ? '#FFFFFF' : 'var(--ex-bg-card)', opacity: c.is_future ? 1 : (light ? 0.9 : 0.72),
    }}>
      <div style={{
        background: c.is_future ? bg : (light ? '#E2E8F0' : 'var(--ex-bg-panel)'),
        color: c.is_future ? '#FFFFFF' : (light ? '#475569' : 'var(--ex-text-secondary)'),
        fontWeight: 800, textAlign: 'center', padding: '5px', fontSize: 13,
      }}>{monLabel(c.month)}</div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '7px 4px', fontSize: 11, lineHeight: 1.65, textAlign: 'center', color: light ? '#101D35' : 'var(--ex-text-primary)' }}>
        <div>תור <b>{c.queue_hours}</b>ש׳</div>
        {c.queue_est_hours > 0 && <div style={{ color: light ? '#8B5CF6' : '#8B5CF6' }}>משוער {c.queue_est_hours}ש׳</div>}
        <div style={{ color: light ? '#475569' : 'var(--ex-text-muted)' }}>בוצע {done}</div>
        <div style={{ color: light ? '#0284C7' : '#0284C7' }}>נקבע {c.sched_fu_hours}/{c.sched_intake_hours}</div>
        <div style={{ color: light ? '#047857' : '#047857' }}>פנוי {c.free_hours}ש׳</div>
        <div style={{ color: light ? '#92400E' : '#92400E' }}>איחור {c.delay_days != null ? `${c.delay_days}י׳` : '—'}</div>
        <div style={{ marginTop: 'auto', paddingTop: 4 }}>
          <div style={{ padding: '3px', borderRadius: 5, background: `${bg}22`, fontWeight: 800, color: bg }}>
            מצטבר {c.cumulative_hours}
          </div>
        </div>
      </div>
    </div>
  );
}
